import { GlAvatar, GlBadge, GlButton, GlIcon, GlNavItem, GlTooltipDirective } from '@gitlab/ui';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { s__, sprintf } from '~/locale';
import { CLICK_MENU_ITEM_ACTION, CLICK_PINNED_MENU_ITEM_ACTION, TRACKING_UNKNOWN_ID, TRACKING_UNKNOWN_PANEL } from '~/super_sidebar/constants';
import { ariaCurrent } from '../utils';
export default {
    i18n: {
        pin: s__('Navigation|Pin %{title}'),
        pinItem: s__('Navigation|Pin item'),
        unpin: s__('Navigation|Unpin %{title}'),
        unpinItem: s__('Navigation|Unpin item')
    },
    name: 'NavItem',
    components: {
        GlAvatar,
        GlBadge,
        GlButton,
        GlIcon,
        GlNavItem
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    inject: {
        pinnedItemIds: {
            default: {
                ids: []
            }
        },
        panelSupportsPins: {
            default: false
        },
        panelType: {
            default: ''
        },
        isIconOnly: {
            default: false
        }
    },
    props: {
        isInPinnedSection: {
            type: Boolean,
            required: false,
            default: false
        },
        isStatic: {
            type: Boolean,
            required: false,
            default: false
        },
        item: {
            type: Object,
            required: true
        },
        linkClasses: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        isSubitem: {
            type: Boolean,
            required: false,
            default: false
        },
        hideIcon: {
            type: Boolean,
            required: false,
            default: false
        },
        isFlyout: {
            type: Boolean,
            required: false,
            default: false
        },
        asyncCount: {
            type: Object,
            required: false,
            default: ()=>({})
        }
    },
    emits: [
        'nav-item-keydown-esc',
        'nav-link-click',
        'nav-pin-keydown-esc',
        'pin-add',
        'pin-remove'
    ],
    data () {
        return {
            isMouseIn: false,
            canClickPinButton: false
        };
    },
    computed: {
        pillData () {
            if (this.item.pill_count_field) {
                const countField = this.item.pill_count_field;
                const hasAsyncCount = Object.prototype.hasOwnProperty.call(this.asyncCount, countField);
                return hasAsyncCount ? this.asyncCount[countField] : '-';
            }
            return this.item.pill_count;
        },
        hasPill () {
            return Number.isFinite(this.pillData) || typeof this.pillData === 'string' && this.pillData !== '';
        },
        isPinnable () {
            return this.panelSupportsPins && !this.isStatic;
        },
        isPinned () {
            return this.pinnedItemIds.ids.includes(this.item.id);
        },
        showPinButton () {
            return this.isPinnable && (!this.isIconOnly || this.isFlyout);
        },
        trackingProps () {
            // Set extra event data to debug missing IDs / Panel Types
            const extraData = !this.item.id || !this.panelType ? {
                'data-track-extra': JSON.stringify({
                    title: this.item.title
                })
            } : {};
            return {
                'data-track-action': this.isInPinnedSection ? CLICK_PINNED_MENU_ITEM_ACTION : CLICK_MENU_ITEM_ACTION,
                'data-track-label': this.item.id ?? TRACKING_UNKNOWN_ID,
                'data-track-property': this.panelType ? `nav_panel_${this.panelType}` : TRACKING_UNKNOWN_PANEL,
                ...extraData
            };
        },
        /**
     * Some QA specs rely on a stable "Project overview"/"Group overview" nav
     * item data-qa-submenu-item attribute value.
     *
     * This computed ensures that those particular nav items use the `id` of
     * the item rather than its title for that QA attribute.
     *
     * In future, probably all nav items should do this, for consistency.
     * See https://gitlab.com/gitlab-org/gitlab/-/issues/422925.
     */ qaSubMenuItem () {
            const { id } = this.item;
            if (id === 'project_overview' || id === 'group_overview') return id.replace(/_/g, '-');
            return this.item.title;
        },
        linkProps () {
            return {
                ...this.$attrs,
                ...this.trackingProps,
                'aria-current': ariaCurrent(this.isActive),
                selected: this.isActive,
                href: this.item.link,
                to: this.item.to,
                'data-qa-submenu-item': this.qaSubMenuItem,
                'data-method': this.item.data_method ?? null
            };
        },
        computedLinkClasses () {
            return {
                'gl-px-2 gl-mx-2 gl-leading-normal': this.isSubitem,
                'gl-px-2': !this.isSubitem,
                '!gl-pl-5 gl-rounded-default': this.isFlyout,
                [this.item.link_classes]: this.item.link_classes,
                ...this.linkClasses
            };
        },
        hasAvatar () {
            return Boolean(this.item.entity_id);
        },
        hasEndSpace () {
            return this.hasPill || this.isPinnable || this.isFlyout;
        },
        avatarShape () {
            return this.item.avatar_shape || 'rect';
        },
        pinAriaLabel () {
            return sprintf(this.$options.i18n.pin, {
                title: this.item.title
            });
        },
        unpinAriaLabel () {
            return sprintf(this.$options.i18n.unpin, {
                title: this.item.title
            });
        },
        isActive () {
            return this.item.is_active;
        },
        hasBadge () {
            return Boolean(this.item.badge);
        },
        itemIcon () {
            if (this.glFeatures.hideUnpinnedSidebarItems) {
                return this.item.library_icon || this.item.icon;
            }
            return this.item.icon;
        }
    },
    mounted () {
        if (this.isActive && !this.isFlyout) {
            this.$el.scrollIntoView({
                behavior: 'instant',
                block: 'center',
                inline: 'nearest'
            });
        }
    },
    methods: {
        pinAdd () {
            // Reset mouse state before emitting to prevent Safari bug where mouseleave
            // doesn't fire when the element is removed from DOM
            this.isMouseIn = false;
            this.$emit('pin-add', this.item.id, this.item.title);
        },
        pinRemove () {
            // Reset mouse state before emitting to prevent Safari bug where mouseleave
            // doesn't fire when the element is removed from DOM
            this.isMouseIn = false;
            this.$emit('pin-remove', this.item.id, this.item.title);
        },
        togglePointerEvents () {
            this.canClickPinButton = this.isMouseIn;
        },
        // eslint-disable-next-line vue/no-unused-properties -- invoked externally via this.$refs.currentPageItem in sidebar_menu.vue
        focus () {
            this.$refs.navLink?.$el?.focus();
        }
    }
};
